import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

function parseValueCell(raw) {
  const t = raw.trim();
  if (t === '') return '';
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

/**
 * Edit `definition.state` (initial global state for the Python executor).
 * Simple tab: flat key / value rows; JSON tab: full object editor.
 */
export default function StudioV2FlowStateEditor({ text, setText }) {
  const [tab, setTab] = useState(0);
  const [localJson, setLocalJson] = useState('{}');
  const [rows, setRows] = useState([{ key: '', value: '' }]);
  const [error, setError] = useState(null);

  const applyStateToDoc = useCallback((stateObj) => {
    if (stateObj === null || typeof stateObj !== 'object' || Array.isArray(stateObj)) {
      setError('State must be a JSON object.');
      return false;
    }
    try {
      const d = JSON.parse(text || '{}');
      const next = { ...d, state: stateObj };
      setText(JSON.stringify(next, null, 2));
      setError(null);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, [text, setText]);

  const syncFromText = useCallback(() => {
    try {
      const d = JSON.parse(text || '{}');
      const st = d?.state;
      const obj = st != null && typeof st === 'object' && !Array.isArray(st) ? st : {};
      setLocalJson(JSON.stringify(obj, null, 2));
      const entries = Object.entries(obj);
      setRows(entries.length ? entries.map(([k, v]) => ({
        key: k,
        value: typeof v === 'string' ? JSON.stringify(v) : JSON.stringify(v, null, 0),
      })) : [{ key: '', value: '' }]);
      setError(null);
    } catch (e) {
      setError(e.message || 'Invalid JSON');
    }
  }, [text]);

  useEffect(() => {
    syncFromText();
  }, [syncFromText]);

  const onBlurJsonCommit = () => {
    let parsed;
    try {
      parsed = JSON.parse(localJson.trim() || '{}');
    } catch (e) {
      setError(e.message);
      return;
    }
    applyStateToDoc(parsed);
  };

  const commitRowsFrom = (rowList) => {
    const next = {};
    for (const r of rowList) {
      const k = String(r.key || '').trim();
      if (!k) continue;
      next[k] = parseValueCell(String(r.value ?? ''));
    }
    applyStateToDoc(next);
  };

  const commitRows = () => commitRowsFrom(rows);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Flow state</Typography>
          <Typography variant="caption" color="text.secondary">
            Maps to
            {' '}
            <code>definition.state</code>
            {' '}
            (Python
            {' '}
            <code>globalState</code>
            ). Use Simple for top-level keys; use JSON for nested objects.
          </Typography>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
          <Tab label="Simple" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab label="JSON" sx={{ minHeight: 36, py: 0.5 }} />
        </Tabs>
        {error && <Alert severity="error">{error}</Alert>}
        {tab === 1 && (
          <TextField
            multiline
            minRows={12}
            fullWidth
            value={localJson}
            onChange={(e) => setLocalJson(e.target.value)}
            onBlur={onBlurJsonCommit}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        )}
        {tab === 0 && (
          <Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="28%">Key</TableCell>
                  <TableCell>Value (JSON or plain string)</TableCell>
                  <TableCell width={48} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ verticalAlign: 'top', py: 0.75 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={r.key}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows((prev) => prev.map((x, j) => (j === i ? { ...x, key: v } : x)));
                        }}
                        onBlur={commitRows}
                        placeholder="variable_name"
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', py: 0.75 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={r.value}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows((prev) => prev.map((x, j) => (j === i ? { ...x, value: v } : x)));
                        }}
                        onBlur={commitRows}
                        placeholder='"hello" or 1 or {"nested":true}'
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top', py: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        onClick={() => {
                          const nextRows = rows.filter((_, j) => j !== i);
                          const normalized = nextRows.length ? nextRows : [{ key: '', value: '' }];
                          setRows(normalized);
                          commitRowsFrom(normalized);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRows((prev) => [...prev, { key: '', value: '' }])}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Add key
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
